// @file Pinia store for the Library LTI app
import { FACE_WITH_ROLLING_EYES_EMOJI } from '@@/bits/emoji'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { HttpCode } from '@@/enums'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import type { Wall } from '@@/types'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { debounce } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

export enum LibraryLtiScreen {
  Success = 'success',
  Error = 'error',
  LinkDeploymentToken = 'link_deployment_token',
  DeepLinkMenu = 'deep_link_menu',
  SelectEnabledDistrictLibrary = 'select_enabled_district_library',
}

// Define type for district library objects
export interface DistrictLibrary {
  id: number
  name: string
  avatarUrl: string | null
  description?: string
}

interface Folder {
  id: number
  name: string
}

export const useLibraryLtiStore = defineStore('libraryLtiStore', () => {
  const globalAlertDialogStore = useGlobalAlertDialogStore()

  const platformIssuer = ref<string>('')
  // State
  const currentScreen = ref<LibraryLtiScreen>(LibraryLtiScreen.LinkDeploymentToken)
  const libraryLtiLaunchUrl = ref<string>('')
  const idToken = ref<string>('')
  const isAwaitingServerResponse = ref<boolean>(false)
  const oauthStateToken = ref<string>('')
  const stateTokenTimeoutSeconds = ref<number>(900)

  // Error screen
  const errorScreenTitle = ref<string>('')
  const errorScreenBody = ref<string>('')
  const actionButtonUrl = ref<string | null>(null)
  const actionButtonText = ref<string | null>(null)

  // Success screen
  const successScreenTitle = ref<string>('')
  const successScreenBody = ref<string>('')

  // Link Deployment Token screen
  const linkDeploymentToken = ref<string>('')
  const tokenValidationMessage = ref<string>('')
  const dashboardSettingsUrl = ref<string>('')

  // District library selection
  const newDistrictLibraryId = ref<number | null>(null)
  const isDistrictDeployment = ref<boolean>(false)

  // Deeplink menu screen
  const deepLinkUrl = ref<string>('')
  const userDisplayName = ref<string>('')
  const username = ref<string>('')
  const avatar = ref<string>('')
  const walls = shallowRef<Wall[]>([])
  const createNewPadletUrl = ref<string>('')
  const openDashboardUrl = ref<string>('')
  const deepLinkTimeoutSeconds = ref<number>(12)
  const libraryName = ref<string>('')
  const currentLibraryId = ref<number>(0)
  // District library selection screen
  const districtLibraries = ref<DistrictLibrary[]>([])
  const folders = shallowRef<Folder[]>([])

  // Getters
  const xLinkDeploymentTokenScreen = computed(
    (): boolean => currentScreen.value === LibraryLtiScreen.LinkDeploymentToken,
  )
  const xDeepLinkMenuScreen = computed((): boolean => currentScreen.value === LibraryLtiScreen.DeepLinkMenu)
  const xErrorScreen = computed((): boolean => currentScreen.value === LibraryLtiScreen.Error)
  const xSuccessScreen = computed((): boolean => currentScreen.value === LibraryLtiScreen.Success)
  const xSelectEnabledDistrictLibraryScreen = computed(
    (): boolean => currentScreen.value === LibraryLtiScreen.SelectEnabledDistrictLibrary,
  )
  const xActionButton = computed((): boolean => actionButtonUrl.value !== null && actionButtonText.value !== null)
  const hasMultipleDistrictLibraries = computed((): boolean => districtLibraries.value.length > 1)

  // Actions

  function initialize(payload: {
    currentScreen: LibraryLtiScreen
    libraryLtiLaunchUrl?: string
    idToken?: string
    oauthStateToken?: string
    wallSearchAccessToken?: string | null
    stateTokenTimeoutSeconds?: number
    deepLinkUrl?: string
    userDisplayName?: string
    username?: string
    libraryName?: string
    currentLibraryId?: number
    avatar?: string
    walls?: Wall[]
    createNewPadletUrl?: string
    openDashboardUrl?: string
    deepLinkTimeoutSeconds?: number
    errorScreenTitle?: string
    errorScreenBody?: string
    successScreenTitle?: string
    successScreenBody?: string
    actionButtonUrl?: string | null
    actionButtonText?: string | null
    platformIssuer?: string | null
    districtLibraries?: DistrictLibrary[]
    isDistrictDeployment?: boolean
    folders?: Folder[]
  }): void {
    currentScreen.value = payload.currentScreen ?? currentScreen.value
    libraryLtiLaunchUrl.value = payload.libraryLtiLaunchUrl ?? libraryLtiLaunchUrl.value
    idToken.value = payload.idToken ?? idToken.value
    oauthStateToken.value = payload.oauthStateToken ?? oauthStateToken.value
    stateTokenTimeoutSeconds.value = payload.stateTokenTimeoutSeconds ?? stateTokenTimeoutSeconds.value
    wallSearchAccessToken.value = payload.wallSearchAccessToken ?? wallSearchAccessToken.value

    deepLinkUrl.value = payload.deepLinkUrl ?? deepLinkUrl.value
    userDisplayName.value = payload.userDisplayName ?? userDisplayName.value
    username.value = payload.username ?? username.value
    libraryName.value = payload.libraryName ?? libraryName.value
    currentLibraryId.value = payload.currentLibraryId ?? currentLibraryId.value
    avatar.value = payload.avatar ?? avatar.value
    walls.value = payload.walls ?? walls.value
    searchResults.value = [...walls.value]
    resetFilter()
    resetSearch()

    createNewPadletUrl.value = payload.createNewPadletUrl ?? createNewPadletUrl.value
    openDashboardUrl.value = payload.openDashboardUrl ?? openDashboardUrl.value
    deepLinkTimeoutSeconds.value = payload.deepLinkTimeoutSeconds ?? deepLinkTimeoutSeconds.value

    errorScreenTitle.value = payload.errorScreenTitle ?? errorScreenTitle.value
    errorScreenBody.value = payload.errorScreenBody ?? errorScreenBody.value
    successScreenTitle.value = payload.successScreenTitle ?? successScreenTitle.value
    successScreenBody.value = payload.successScreenBody ?? successScreenBody.value
    actionButtonUrl.value = payload.actionButtonUrl ?? actionButtonUrl.value
    actionButtonText.value = payload.actionButtonText ?? actionButtonText.value
    platformIssuer.value = payload.platformIssuer ?? platformIssuer.value
    districtLibraries.value = payload.districtLibraries ?? districtLibraries.value
    isDistrictDeployment.value = payload.isDistrictDeployment ?? isDistrictDeployment.value
    folders.value = payload.folders ?? []

    // Initialize newDistrictLibraryId to currentLibraryId if not already set
    // This ensures the current library appears as selected in the UI initially
    if (newDistrictLibraryId.value === null && currentLibraryId.value > 0) {
      newDistrictLibraryId.value = currentLibraryId.value
    }
  }

  function setExpiryErrorScreen(): void {
    currentScreen.value = LibraryLtiScreen.Error
    errorScreenTitle.value = __('The page has expired.')
    errorScreenBody.value = __('Please close this page and open it again.')
  }

  function setLinkDeploymentToken(value: string): void {
    linkDeploymentToken.value = value
  }

  function setTokenValidationMessage(message: string): void {
    tokenValidationMessage.value = message
  }

  async function loginWithToken(): Promise<void> {
    if (linkDeploymentToken.value === '') return

    try {
      isAwaitingServerResponse.value = true

      const response: {
        oauthStateToken: string
        idToken: string
        currentScreen: LibraryLtiScreen
      } = await fetchJson(libraryLtiLaunchUrl.value, {
        method: HTTPMethod.post,
        jsonData: {
          token: linkDeploymentToken.value,
          id_token: idToken.value,
          state: oauthStateToken.value,
        },
      })
      initialize(response)
      tokenValidationMessage.value = ''
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const error = parsedErrorMessage.error

          if (error.error_code === 'INVALID_TOKEN') {
            tokenValidationMessage.value = error.token
          } else {
            tokenValidationMessage.value = error.token
            globalAlertDialogStore.openAlertDialog({
              ...FACE_WITH_ROLLING_EYES_EMOJI,
              title: __('Unable to log in'),
              body: error.token,
              closeButtonText: __('Okay'),
            })
          }
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else {
        captureFetchException(e, { source: 'LibraryLTILinkDeploymentTokenWithToken' })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  async function selectDistrictLibrary(): Promise<void> {
    if (newDistrictLibraryId.value === null) return

    try {
      isAwaitingServerResponse.value = true

      const response = await fetchJson(libraryLtiLaunchUrl.value, {
        method: HTTPMethod.post,
        jsonData: {
          new_district_library_id: newDistrictLibraryId.value,
          id_token: idToken.value,
          state: oauthStateToken.value,
        },
      })

      // Cast response to the correct type
      initialize(
        response as {
          currentScreen: LibraryLtiScreen
          [key: string]: any
        },
      )
    } catch (e) {
      captureFetchException(e, { source: 'LibraryLTISelectDistrictLibrary' })
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  function setSelectedLibraryId(id: number): void {
    newDistrictLibraryId.value = id
  }

  async function saveNewDistrictLibraryAndLaunchDeepLinkMenu(newDistrictLibraryId: number): Promise<void> {
    if (newDistrictLibraryId === null || newDistrictLibraryId === undefined) return

    try {
      isAwaitingServerResponse.value = true

      // Use the libraryLtiLaunchUrl which should point to the correct endpoint
      // This endpoint is the library_lti_launch route which maps to webhooks/library_lti_advantage#launch
      const response = await fetchJson(libraryLtiLaunchUrl.value, {
        method: HTTPMethod.post,
        jsonData: {
          new_district_library_id: newDistrictLibraryId,
          id_token: idToken.value,
          state: oauthStateToken.value,
        },
      })

      // Cast response to the correct type and initialize store
      initialize(
        response as {
          currentScreen: LibraryLtiScreen
          [key: string]: any
        },
      )
    } catch (e) {
      captureFetchException(e, { source: 'LibraryLTISaveSelectedLibraryAndLaunchDeepLinkMenu' })
      // Show error to user
      globalAlertDialogStore.openAlertDialog({
        ...FACE_WITH_ROLLING_EYES_EMOJI,
        title: __('Unable to switch library'),
        body: __('There was an error switching libraries. Please try again.'),
        closeButtonText: __('Okay'),
      })
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  // #region Search & filter walls
  const searchResults = shallowRef<Wall[]>([...walls.value])

  const wallFilterOptions = computed(() => {
    const staticOptions = [
      { label: __('Recents'), value: 'recents', icon: 'recent_outline' },
      { label: __('All padlets'), value: 'all', icon: 'padlets' },
      { label: __('Made by me'), value: 'made', icon: 'made_outline' },
      { label: __('Favorites'), value: 'favorites', icon: 'folder_heart_outline' },
    ]

    const folderOptions = folders.value.map((folder) => ({
      label: folder.name,
      value: `folder:${folder.id}`,
      icon: 'folder_outline',
    }))

    return [...staticOptions, ...folderOptions]
  })

  const DEFAULT_FILTER = 'recents' as const
  const selectedWallFilter = ref<string>(DEFAULT_FILTER)
  const isFilteringWalls = ref<boolean>(false)
  const hasFilterError = ref<boolean>(false)

  const isFilterActive = computed<boolean>(() => selectedWallFilter.value !== DEFAULT_FILTER)

  async function filterWalls(filterValue: string): Promise<void> {
    if (!filterValue) {
      resetFilter()
      return
    }

    selectedWallFilter.value = filterValue
    isFilteringWalls.value = true
    hasFilterError.value = false

    try {
      const isFolderFilter = filterValue.startsWith('folder:')
      const query: Record<string, string> = {
        id_token: idToken.value,
        page_size: '100', // Hardcode the page size for now.
        // User can always refine further with search.
      }

      if (isFolderFilter) {
        query.folder_id = filterValue.replace('folder:', '')
      } else {
        query.filter = filterValue
      }

      const response: { data: { attributes: Wall }[] } = await fetchJson('/api/1/library-lti/walls', {
        headers: { Authorization: wallSearchAccessToken.value ?? '' },
        query,
      })

      const fetchedWalls = response.data.map((item) => item.attributes)

      // Guard against race condition: Only apply recents results if user hasn't switched to another filter
      if (filterValue === DEFAULT_FILTER && selectedWallFilter.value === DEFAULT_FILTER) {
        walls.value = fetchedWalls
        searchResults.value = [...fetchedWalls]
        resetFilter()
      } else if (isFilterActive.value) {
        // Guard against stale updates: Only apply filtered results if still actively filtering
        searchResults.value = fetchedWalls
      }
    } catch (e) {
      hasFilterError.value = true
      captureFetchException(e, { source: 'LibraryLTIFilterWalls' })
    } finally {
      isFilteringWalls.value = false
    }
  }

  function resetFilter(): void {
    selectedWallFilter.value = DEFAULT_FILTER
    isFilteringWalls.value = false
    hasFilterError.value = false
  }

  const wallSearchAccessToken = ref<string | null>(null)
  const searchQuery = ref<string>('')
  const isSearching = ref<boolean>(false)
  const hasSearchError = ref<boolean>(false)

  const isSearchActive = computed<boolean>(() => searchQuery.value.trim().length > 0)

  async function searchWalls(query: string): Promise<void> {
    if (!query || query.trim().length === 0) {
      resetSearch()
      return
    }

    searchQuery.value = query
    isSearching.value = true
    hasSearchError.value = false

    try {
      const response: { data: { attributes: Wall }[] } = await fetchJson('/api/1/library-lti/walls/search', {
        headers: { Authorization: wallSearchAccessToken.value ?? '' },
        query: { id_token: idToken.value, q: query },
      })

      // Only update results if still actively searching
      if (isSearchActive.value) {
        searchResults.value = response.data.map((item) => item.attributes)
      }
    } catch (e) {
      hasSearchError.value = true
      captureFetchException(e, { source: 'LibraryLTISearchWalls' })
    } finally {
      isSearching.value = false
    }
  }

  function resetSearch(): void {
    searchQuery.value = ''
    isSearching.value = false
    hasSearchError.value = false
  }

  // #region Refresh wall list
  async function actuallyRefreshCurrentWallList(): Promise<void> {
    if (isSearchActive.value) {
      await searchWalls(searchQuery.value)
    } else {
      await filterWalls(selectedWallFilter.value)
    }
  }

  let refreshIntervalId: ReturnType<typeof setInterval> | null = null

  const debouncedRefreshCurrentWallList = debounce(() => void actuallyRefreshCurrentWallList(), 1000, {
    edges: ['leading'],
  })

  function refreshCurrentWallList({ intervalMs }: { intervalMs?: number } = {}): void {
    if (intervalMs && intervalMs > 0) {
      stopRefreshCurrentWallList()
      debouncedRefreshCurrentWallList()
      refreshIntervalId = setInterval(() => void actuallyRefreshCurrentWallList(), intervalMs)
    } else {
      debouncedRefreshCurrentWallList()
    }
  }

  function stopRefreshCurrentWallList(): void {
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId)
      refreshIntervalId = null
    }
    debouncedRefreshCurrentWallList.cancel()
  }
  // #endregion

  /**
   * Whether the user is currently typing a search query, a search request is in progress,
   * or a filter request is in progress. Once such request is complete, this becomes false.
   */
  const isSearchingOrFiltering = computed<boolean>(() => isSearching.value || isFilteringWalls.value)

  /**
   * Whether either a search query is active or a filter is active. Or in other words,
   * the wall list is currently being filtered in some way.
   */
  const isSearchOrFilterActive = computed<boolean>(() => isSearchActive.value || isFilterActive.value)

  const hasSearchOrFilterError = computed<boolean>(() => hasSearchError.value || hasFilterError.value)

  const displayedWalls = computed<Wall[]>(() => {
    // While searching/filtering is happening, still show the current results.
    // Once that's done, new results will have been populated into searchResults
    // so we also show those.
    if (isSearchingOrFiltering.value || isSearchOrFilterActive.value) {
      return searchResults.value
    }

    // Otherwise, show the initial walls list.
    return walls.value
  })
  // #endregion

  return {
    // State
    currentScreen,
    stateTokenTimeoutSeconds,
    isAwaitingServerResponse,
    tokenValidationMessage,
    linkDeploymentToken,
    dashboardSettingsUrl,
    newDistrictLibraryId,
    isDistrictDeployment,

    userDisplayName,
    username,
    libraryName,
    currentLibraryId,
    avatar,
    createNewPadletUrl,
    openDashboardUrl,
    deepLinkUrl,
    deepLinkTimeoutSeconds,
    walls,
    errorScreenTitle,
    errorScreenBody,
    successScreenTitle,
    successScreenBody,
    actionButtonUrl,
    actionButtonText,
    platformIssuer,
    districtLibraries,

    // Getters
    xLinkDeploymentTokenScreen,
    xDeepLinkMenuScreen,
    xErrorScreen,
    xSuccessScreen,
    xSelectEnabledDistrictLibraryScreen,
    xActionButton,
    hasMultipleDistrictLibraries,

    // Actions
    initialize,
    setExpiryErrorScreen,
    setLinkDeploymentToken,
    setTokenValidationMessage,
    loginWithToken,
    setSelectedLibraryId,
    selectDistrictLibrary,
    saveNewDistrictLibraryAndLaunchDeepLinkMenu,

    // Filter & search walls
    DEFAULT_FILTER,
    wallFilterOptions,
    selectedWallFilter,
    isFilteringWalls,
    isFilterActive,
    filterWalls,
    resetFilter,

    searchQuery,
    isSearching,
    isSearchActive,
    searchWalls,
    resetSearch,
    refreshCurrentWallList,
    stopRefreshCurrentWallList,

    isSearchingOrFiltering,
    isSearchOrFilterActive,
    hasSearchOrFilterError,
    displayedWalls,
  }
})
